import { useRequireSignIn } from "@/hooks/use-require-sign-in";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Sparkles, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { saveIntake, advanceOnboarding } from "@/lib/onboarding.functions";
import { friendlyAuthError } from "@/lib/friendly-errors";

export const Route = createFileRoute("/personalise")({
  head: () => ({
    meta: [
      { title: "Personalise your experience — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A short 60-second intake so we can tailor your first Recovery Trauma™ resources to how you're feeling right now.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PersonalisePage,
});

const FEELINGS = [
  { value: "overwhelmed", label: "Overwhelmed", hint: "A lot is piling up right now." },
  { value: "anxious", label: "Anxious", hint: "My body feels alert or on edge." },
  { value: "numb", label: "Numb / flat", hint: "I feel disconnected or foggy." },
  { value: "stuck", label: "Stuck", hint: "I keep circling the same place." },
  { value: "grieving", label: "Grieving", hint: "I'm sitting with a loss." },
  { value: "hopeful", label: "Hopeful, curious", hint: "I'm ready to explore gently." },
];

const PRIORITIES = [
  { value: "safety", label: "Feel safer in my body", hint: "Grounding, breathing, calm." },
  { value: "regulate", label: "Regulate strong emotions", hint: "Ride the wave without drowning." },
  { value: "process", label: "Process past experiences", hint: "Slowly, at my pace." },
  { value: "routine", label: "Build a gentle daily rhythm", hint: "Small habits that hold me." },
  { value: "relationships", label: "Feel steadier in relationships", hint: "Boundaries and connection." },
  { value: "sleep", label: "Sleep and rest better", hint: "Wind down a wired system." },
];

const INTERESTS = [
  "Anxiety",
  "CPTSD",
  "Childhood Trauma",
  "Flashbacks",
  "Emotional Regulation",
  "Grounding",
  "Breathing",
  "Somatic Healing",
  "Sleep",
  "Dissociation",
  "Panic",
  "Boundaries",
  "Self-Esteem",
  "Relationships",
  "Inner Child",
  "Grief",
  "Attachment",
  "Burnout",
];

type Step = 0 | 1 | 2;

function PersonalisePage() {
  const signedIn = useRequireSignIn();
  if (!signedIn) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6 text-sm text-muted-foreground" role="status">
        Checking you're signed in…
      </div>
    );
  }
  return <PersonalisePageInner />;
}

function PersonalisePageInner() {
  const navigate = useNavigate();
  const save = useServerFn(saveIntake);
  const advance = useServerFn(advanceOnboarding);

  const [step, setStep] = useState<Step>(0);
  const [feeling, setFeeling] = useState<string>("");
  const [priority, setPriority] = useState<string>("");
  const [interests, setInterests] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function toggleInterest(name: string) {
    setInterests((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  async function finish() {
    setErr(null);
    setSaving(true);
    try {
      await save({
        data: {
          feeling: feeling || "unspecified",
          priority: priority || "unspecified",
          interests: Array.from(interests),
        },
      });
      navigate({ to: "/dashboard", replace: true });
    } catch (e) {
      setErr(friendlyAuthError(e));
    } finally {
      setSaving(false);
    }
  }

  async function skipAll() {
    setErr(null);
    setSaving(true);
    try {
      await advance({ data: { step: "done" } });
      navigate({ to: "/dashboard", replace: true });
    } catch (e) {
      setErr(friendlyAuthError(e));
    } finally {
      setSaving(false);
    }
  }

  const canNext =
    (step === 0 && !!feeling) || (step === 1 && !!priority) || step === 2;
  const stepLabels = ["How you feel", "Your priority", "Areas of focus"];

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:py-14">
      <div className="text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
          <Sparkles className="h-6 w-6 text-foreground" aria-hidden="true" />
        </div>
        <h1 className="mt-5 font-display text-2xl text-foreground sm:text-3xl">
          Let's tailor your first steps
        </h1>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground/75">
          Three short questions — about 60 seconds. Your answers stay private
          and shape the resources we surface first. You can change them any
          time in your profile.
        </p>
      </div>

      {/* Progress */}
      <ol className="mt-8 flex items-center justify-center gap-2" aria-label="Onboarding progress">
        {stepLabels.map((label, i) => {
          const active = i === step;
          const done = i < step;
          return (
            <li key={label} className="flex items-center gap-2">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold ${
                  done
                    ? "border-gold bg-[var(--gradient-gold)] text-foreground"
                    : active
                      ? "border-gold bg-cream text-foreground"
                      : "border-border bg-background text-foreground/40"
                }`}
                aria-current={active ? "step" : undefined}
              >
                {done ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : i + 1}
              </span>
              <span className={`hidden text-xs sm:inline ${active ? "text-foreground" : "text-foreground/50"}`}>
                {label}
              </span>
              {i < stepLabels.length - 1 && (
                <span className="mx-1 h-px w-6 bg-border sm:w-10" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>

      <div className="mt-8">
        {step === 0 && (
          <StepRadio
            legend="How are you feeling right now?"
            name="feeling"
            options={FEELINGS}
            value={feeling}
            onChange={setFeeling}
          />
        )}
        {step === 1 && (
          <StepRadio
            legend="Right now, your biggest priority is…"
            name="priority"
            options={PRIORITIES}
            value={priority}
            onChange={setPriority}
          />
        )}
        {step === 2 && (
          <fieldset>
            <legend className="mb-4 text-center font-display text-lg text-foreground">
              Which areas would you like guidance on?
            </legend>
            <p className="mb-4 text-center text-xs text-foreground/60">
              Pick as many as feel right — optional.
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {INTERESTS.map((name) => {
                const active = interests.has(name);
                return (
                  <label
                    key={name}
                    className={`flex min-h-[52px] cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${
                      active
                        ? "border-gold bg-cream/70 text-foreground shadow-[var(--shadow-gold)]"
                        : "border-border bg-card text-foreground/80 hover:border-gold/50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleInterest(name)}
                      className="h-4 w-4 accent-[var(--gold-deep)]"
                    />
                    <span>{name}</span>
                  </label>
                );
              })}
            </div>
            <p className="mt-4 text-center text-xs text-muted-foreground">
              {interests.size === 0 ? "No selections yet" : `${interests.size} selected`}
            </p>
          </fieldset>
        )}
      </div>

      {err && (
        <p className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {err}
        </p>
      )}

      <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-3">
          {step > 0 ? (
            <button
              type="button"
              onClick={() => setStep((s) => (s - 1) as Step)}
              disabled={saving}
              className="inline-flex min-h-[48px] items-center gap-2 rounded-full border border-border bg-background px-5 py-3 text-sm font-medium text-foreground/80 disabled:opacity-60"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
            </button>
          ) : (
            <button
              type="button"
              onClick={skipAll}
              disabled={saving}
              className="inline-flex min-h-[48px] items-center justify-center rounded-full border border-border bg-background px-5 py-3 text-sm font-medium text-foreground/70 disabled:opacity-60"
            >
              Skip for now
            </button>
          )}
        </div>

        {step < 2 ? (
          <button
            type="button"
            onClick={() => canNext && setStep((s) => (s + 1) as Step)}
            disabled={!canNext || saving}
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-6 py-3 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)] disabled:opacity-60"
          >
            Continue <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            onClick={finish}
            disabled={saving}
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-6 py-3 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)] disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Show my tailored dashboard <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

type Option = { value: string; label: string; hint: string };

function StepRadio({
  legend,
  name,
  options,
  value,
  onChange,
}: {
  legend: string;
  name: string;
  options: Option[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-4 text-center font-display text-lg text-foreground">
        {legend}
      </legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3 text-start transition ${
                active
                  ? "border-gold bg-cream/70 text-foreground shadow-[var(--shadow-gold)]"
                  : "border-border bg-card text-foreground/80 hover:border-gold/50"
              }`}
            >
              <input
                type="radio"
                name={name}
                checked={active}
                onChange={() => onChange(opt.value)}
                className="mt-1 h-4 w-4 accent-[var(--gold-deep)]"
              />
              <span className="flex-1">
                <span className="block text-sm font-medium">{opt.label}</span>
                <span className="mt-0.5 block text-xs text-foreground/60">{opt.hint}</span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
